import { ErrorMessage, Field, useField } from 'formik'
import type { ReactNode } from 'react'
import { useId } from 'react'
import styles from './OptionGroup.module.scss'

export type OptionGroupOption = {
  value: string
  label: string
}

type OptionGroupProps = {
  name: string
  options: OptionGroupOption[]
  legend: ReactNode
}

/**
 * Formik-connected radio group with options and error display.
 */
export const OptionGroup = ({ name, options, legend }: OptionGroupProps) => (
  <OptionGroupField name={name} options={options} legend={legend} />
)

/**
 * Render radio options with grouped semantics and assistive error association.
 */
const OptionGroupField = ({ name, options, legend }: OptionGroupProps) => {
  const idPrefix = useId().replace(/:/g, '')
  const [, meta] = useField<string>(name)
  const hasError = Boolean(meta.touched && meta.error)
  const errorId = `${idPrefix}-${name}-error`

  return (
    <div>
      <fieldset
        className={styles.fieldset}
        aria-describedby={hasError ? errorId : undefined}
        aria-invalid={hasError}
      >
        <legend className={styles.legendSrOnly}>{legend}</legend>
        <div className={styles.optionGroup}>
          {options.map((option) => {
            const optionId = `${idPrefix}-${name}-${option.value}`
            return (
              <label key={option.value} htmlFor={optionId} className={styles.option}>
                <Field
                  type="radio"
                  name={name}
                  id={optionId}
                  value={option.value}
                  className={styles.input}
                  aria-describedby={hasError ? errorId : undefined}
                  aria-invalid={hasError}
                />
                <span className={styles.optionLabel}>{option.label}</span>
              </label>
            )
          })}
        </div>
      </fieldset>
      <ErrorMessage name={name}>
        {(msg) => (
          <div className={styles.error} id={errorId} role="alert">
            {msg}
          </div>
        )}
      </ErrorMessage>
    </div>
  )
}
